import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './MobileMyDocumentsCard.css';
import { useAuth } from '../../contexts/AuthContext';
import { DocumentApi } from '../../services/ApiService';
import { getAudioDocumentTypeIds } from '../../utils/documentTypeUtils';

export default function MobileMyDocumentsCard({
  selectionMode = true,
  selectedDocumentIds = [],
  onToggleSelect,
  onSelectionChange,
  requiredStatusIds = [],
  pinnedDocumentTypeId = null,
  endeavorId = null // optional: scope to a specific course
}){
  const { user } = useAuth();
  const navigate = useNavigate();
  const userId = user?.id || user?.userId;

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState(pinnedDocumentTypeId ? String(pinnedDocumentTypeId) : 'all');
  const [sort, setSort] = useState('date');

  // Load user documents (optionally scoped by endeavorId)
  useEffect(() => {
    let abort = false;
    if (!userId) return;

    const load = async () => {
      try {
        setLoading(true);
        setErr(null);
        const audioTypeIds = await getAudioDocumentTypeIds();
        const params = { 
          page: 0, 
          size: 250,
          excludeDocumentTypeIds: audioTypeIds
        };
        if (endeavorId) params.endeavorId = Number(endeavorId);
        const resp = await DocumentApi.getDocumentsByUser(userId, params);
        if (!resp.ok) throw new Error(`Failed (${resp.status})`);
        const data = await resp.json();
        const list = Array.isArray(data) ? data : (data.content || data.documents || []);
        if (abort) return;
        setDocs(list);
      } catch (e) {
        if (!abort) setErr(e);
      } finally {
        if (!abort) setLoading(false);
      }
    };
    load();
    return () => { abort = true; };
  }, [userId, endeavorId]);

  const filtered = useMemo(() => {
    let list = docs;

    // Client-side scope in case backend didn't filter by endeavorId
    if (endeavorId) {
      list = list.filter(d => {
        const ids = (d.endeavorIds || []).map(String);
        return ids.includes(String(endeavorId));
      });
    }

    if (Array.isArray(requiredStatusIds) && requiredStatusIds.length > 0) {
      list = list.filter(d => {
        const ids = (d.statuses || []).map(s => s.id || s.statusId);
        return requiredStatusIds.every(req => ids.includes(Number(req)));
      });
    }

    if (typeFilter !== 'all') {
      list = list.filter(d => String(d.documentTypeId || d.docTypeId) === String(typeFilter));
    }

    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter(d =>
        (d.name || '').toLowerCase().includes(q) ||
        (d.endeavorName || '').toLowerCase().includes(q)
      );
    }
    const sorted = [...list];
    sorted.sort((a,b) => {
      switch (sort) {
        case 'name': return (a.name || '').localeCompare(b.name || '');
        case 'course': return (a.endeavorName || '').localeCompare(b.endeavorName || '');
        case 'date':
        default:
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      }
    });
    return sorted;
  }, [docs, query, typeFilter, sort, requiredStatusIds, endeavorId]);

  const visibleIds = filtered.map(d => String(d.documentId || d.id)).filter(Boolean);
  const allVisibleSelected = selectionMode && visibleIds.length > 0 && visibleIds.every(id => selectedDocumentIds.includes(id));

  const toggleSelectAllVisible = () => {
    if (!selectionMode) return;
    if (allVisibleSelected) {
      const next = selectedDocumentIds.filter(id => !visibleIds.includes(id));
      onSelectionChange ? onSelectionChange(next) : visibleIds.forEach(id => onToggleSelect?.(id));
    } else {
      const set = new Set(selectedDocumentIds);
      visibleIds.forEach(id => set.add(id));
      onSelectionChange ? onSelectionChange([...set]) : visibleIds.forEach(id => onToggleSelect?.(id));
    }
  };

  return (
    <div className="m-docs-card">
      <div className="m-docs-controls">
        <input
          className="m-docs-search"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search documents"
          aria-label="Search documents"
        />
        <select
          className="m-docs-filter"
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
          disabled={!!pinnedDocumentTypeId}
          title={pinnedDocumentTypeId ? 'Document type is pinned' : undefined}
        >
          <option value="all">All Types</option>
          {/* Build type list from docs */}
          {[...new Map(
            docs.map(d => [String(d.documentTypeId || d.docTypeId), (d.documentTypeName || d.documentType || d.typeName || `Type ${d.documentTypeId || d.docTypeId}`)])
          ).entries()].map(([id, name]) => (
            <option key={id} value={id}>{name}</option>
          ))}
        </select>
        <select className="m-docs-sort" value={sort} onChange={e => setSort(e.target.value)}>
          <option value="date">Newest</option>
          <option value="name">Name</option>
          <option value="course">Course</option>
        </select>
      </div>

      {selectionMode && (
        <div className="m-docs-bulk">
          <label>
            <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAllVisible} />
            Select visible ({visibleIds.length})
          </label>
        </div>
      )}

      <div className="m-docs-list">
        {loading ? (
          <div className="m-docs-empty">Loading…</div>
        ) : err ? (
          <div className="m-docs-empty">Failed to load documents.</div>
        ) : filtered.length === 0 ? (
          <div className="m-docs-empty">No documents found.</div>
        ) : (
          filtered.map(d => {
            const id = String(d.documentId || d.id);
            const checked = selectedDocumentIds.includes(id);

            const handleItemClick = async (e) => {
              if (selectionMode) {
                onToggleSelect?.(id);
                return;
              }
              // view mode: navigate to mobile document view
              console.log('MobileMyDocumentsCard: Navigating to document view with ID:', id, 'Document object:', d);
              navigate(`/m/document/${id}`);
            };

            return (
              <button
                key={id}
                type="button"
                className={`m-docs-item ${checked ? 'selected' : ''}`}
                onClick={handleItemClick}
                aria-pressed={checked}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggleSelect?.(id)}
                  onClick={e => e.stopPropagation()}
                  style={{ display: selectionMode ? undefined : 'none' }}
                />
                <div className="m-docs-meta">
                  {/* Single-line name only */}
                  <div className="m-docs-name" title={d.name}>{d.name || 'Untitled'}</div>
                  {/* optional smaller course/meta (kept hidden per CSS) */}
                  <div className="m-docs-sub">{d.endeavorName || d.course || ''}</div>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}